import {
  Box,
  Button,
  Flex,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  Tooltip,
} from '@chakra-ui/react';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { FC, useCallback, useEffect, useState } from 'react';
import { AttributeValue } from '../useFacets';

type Props = {
  facetValues: AttributeValue[];
  selectedValues: string[];
  addRangeFilter: (min: number, max: number) => void;
  reset: () => void;
};

const determineStep = (maxRange: number): number => {
  if (maxRange <= 0) return 1;

  const orderOfMagnitude = Math.floor(Math.log10(maxRange));
  const step = Math.pow(10, orderOfMagnitude);

  // example: if the maxRange is 43, the output will be 1
  // example: if the maxRange is 22130, the output will be 1000
  return Math.max(step / 10, 1);
};

const AttributeFacetSlider: FC<Props> = ({
  facetValues,
  selectedValues,
  addRangeFilter: setValues,
  reset,
}) => {
  const { t } = useTranslation('search');
  const min = Number(facetValues[0].value);
  const max = Number(facetValues[facetValues.length - 1].value);
  const [range, setRange] = useState<number[]>(
    selectedValues.length === 1
      ? [Number(selectedValues[0]), Number(selectedValues[0])]
      : selectedValues.length >= 2
      ? [Number(selectedValues[0]), Number(selectedValues[selectedValues.length - 1])]
      : [min, max],
  );

  const updateRange = (values: [number, number]) => {
    handleSetValues(values);
    setRange(values);
  };

  const handleSetValues = (values: [number, number]) => {
    setValues(values[0], values[1]);
  };

  const handleResetValues = useCallback(() => {
    // Reset the range if the clear button is clicked
    reset();
    setRange([min, max]);
  }, [max, min, reset]);

  useEffect(() => {
    // Reset the range if the path changes
    if (!!selectedValues.length) return;
    setRange([min, max]);
  }, [max, min, selectedValues.length]);

  return (
    <Flex
      flexDir="column"
      alignItems="flex-start"
      gap="space.12"
      p="space.16"
      border="1px solid"
      rounded="md"
      borderColor="border"
    >
      <Box w="full">
        <RangeSlider
          onChange={setRange}
          onChangeEnd={handleSetValues}
          // eslint-disable-next-line jsx-a11y/aria-proptypes
          aria-label={['min', 'max']}
          value={range}
          defaultValue={range}
          min={min}
          max={max}
          step={determineStep(max)}
        >
          <RangeSliderTrack h={rem(6)} rounded="full" bg="gray.4">
            <RangeSliderFilledTrack bg="gray.1" />
          </RangeSliderTrack>
          <Tooltip label={String(range[0])} arrowSize={8} hasArrow placement="top" bg="blackBg">
            <RangeSliderThumb index={0} boxSize={rem(20)} bg="white" />
          </Tooltip>
          <Tooltip label={String(range[1])} arrowSize={8} hasArrow placement="top" bg="blackBg">
            <RangeSliderThumb index={1} boxSize={rem(20)} bg="white" />
          </Tooltip>
        </RangeSlider>
        <Flex justify="space-between">
          <Button
            as="span"
            role="button"
            variant="link"
            fontSize="xs"
            justifyContent="flex-start"
            onClick={() => updateRange([min, range[1]])}
          >
            {min}
          </Button>
          <Button
            as="span"
            role="button"
            variant="link"
            fontSize="xs"
            justifyContent="flex-end"
            onClick={() => updateRange([range[0], max])}
          >
            {max}
          </Button>
        </Flex>
      </Box>
      <Button
        as="span"
        role="button"
        variant="link"
        fontSize="xs"
        w="full"
        fontWeight="medium"
        color="white"
        justifyContent="flex-start"
        onClick={handleResetValues}
      >
        {t('facets.actions.clear')}
      </Button>
    </Flex>
  );
};

export default AttributeFacetSlider;
